import React, { useState } from "react";
import "./resetPassword.css";
import { resetPassword } from "../../../services/authAPI";


interface ResetPasswordProps {
  resetEmail: string;
  setDialogView: React.Dispatch<React.SetStateAction<"signin" | "forgot" | "otp" | "reset">>;
}

const ResetPassword = ({ resetEmail, setDialogView }: ResetPasswordProps) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ newPassword?: string; confirmPassword?: string }>({});
  const [success, setSuccess] = useState(false);

  const validate = () => {
    const errs: { newPassword?: string; confirmPassword?: string } = {};

    if (!newPassword) {
      errs.newPassword = "New password is required";
    } else if (newPassword.length < 8) {
      errs.newPassword = "Password must be at least 8 characters";
    } else if (!/(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])/.test(newPassword)) {
      errs.newPassword = "Must include uppercase, number and special character";
    }

    if (!confirmPassword) {
      errs.confirmPassword = "Please confirm your password";
    } else if (newPassword !== confirmPassword) {
      errs.confirmPassword = "Passwords do not match";
    }

    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    try {
      setLoading(true);
      setErrors({});

      await resetPassword({
        email: resetEmail,
        newPassword,
        confirmPassword,
      });

      setSuccess(true);

      setTimeout(() => {
        setDialogView("signin");
      }, 2000);
    } catch (err: any) {
      setErrors({
        newPassword: err.message || "Failed to reset password. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='page-wrapper'>
      <main className='main-content'>
        <div className='login-card'>
          {/* Left Panel */}
          <div className='left-panel'>
            <div className='left-panel-content'>
              <h2 className='welcome-title'>
                Reset Password <span className='heart-icon'>🔑</span>
              </h2>
              <p className='welcome-subtitle'>
                Create a strong new password for <strong className='rp-email-highlight'>{resetEmail}</strong>. Make sure
                it's something you'll remember.
              </p>
            </div>
          </div>

          {/* Right Panel */}
          <div className='right-panel'>
            <h1 className='login-heading'>New Password</h1>
            <p className='login-subheading'>Set a new password for your account</p>

            <div className='login-form'>
              {/* New Password */}
              <div className='form-group'>
                <label className='form-label'>New Password</label>
                <input
                  type='password'
                  className={`form-input${errors.newPassword ? " input-error" : ""}`}
                  placeholder='Enter new password'
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setErrors((prev) => ({ ...prev, newPassword: undefined }));
                  }}
                  disabled={loading || success}
                />
                {errors.newPassword && <span className='field-error-msg'>{errors.newPassword}</span>}
              </div>

              {/* Confirm Password */}
              <div className='form-group'>
                <label className='form-label'>Confirm Password</label>
                <input
                  type='password'
                  className={`form-input${errors.confirmPassword ? " input-error" : ""}`}
                  placeholder='Re-enter new password'
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
                  }}
                  disabled={loading || success}
                />
                {errors.confirmPassword && <span className='field-error-msg'>{errors.confirmPassword}</span>}
              </div>

              {/* Password strength hint */}
              <p className='rp-hint'>Must be 8+ characters with uppercase, number and special character.</p>

              {/* Success */}
              {success && <p className='success-message'>✅ Password reset successful! Redirecting to login...</p>}

              {/* Submit */}
              <button
                className={`login-btn${loading ? " loading" : ""}`}
                onClick={handleSubmit}
                disabled={loading || success}
              >
                {loading ? <span className='spinner' /> : <>Reset Password</>}
              </button>

              {/* Back */}
              <p className='register-prompt'>
                <a
                  href='#'
                  className='register-link'
                  onClick={(e) => {
                    e.preventDefault();
                    setDialogView("otp");
                  }}
                >
                  Back to OTP
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
